import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, ReferralInfo, Character } from '../contexts/AuthContext';

interface ReferralPanelProps {
  onClose: () => void;
  currentCharacterId: string;
  characters: Character[];
}

export const ReferralPanel: React.FC<ReferralPanelProps> = ({ onClose, currentCharacterId, characters }): React.ReactElement => {
  const auth = useAuth();
  const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [referralCodeInput, setReferralCodeInput] = useState<string>('');
  const [applyError, setApplyError] = useState<string | null>(null);
  const [applySuccess, setApplySuccess] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState<boolean>(false);
  const [isClaiming, setIsClaiming] = useState<boolean>(false);
  const [claimMessage, setClaimMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState<boolean>(false);
  const [selectedCharacterId] = useState<string>(currentCharacterId);

  const loadReferralInfo = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    const info = await auth.getReferralInfo();
    setReferralInfo(info);
    setIsLoading(false);
  }, [auth]);

  useEffect(() => {
    loadReferralInfo();
  }, [loadReferralInfo]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleCopyCode = (): void => {
    if (referralInfo?.referralCode) {
      navigator.clipboard.writeText(referralInfo.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleApplyCode = async (): Promise<void> => {
    if (!referralCodeInput.trim()) return;

    setIsApplying(true);
    setApplyError(null);
    setApplySuccess(null);

    const result = await auth.applyReferralCode(referralCodeInput.trim(), selectedCharacterId);

    if (result.success) {
      setApplySuccess('Referral code applied! You will receive 250,000 pieces.');
      setReferralCodeInput('');
      loadReferralInfo();
    } else {
      setApplyError(result.error || 'Failed to apply code');
    }

    setIsApplying(false);
  };

  const handleClaimRewards = async (): Promise<void> => {
    setIsClaiming(true);
    setClaimMessage(null);

    const result = await auth.claimReferralRewards(selectedCharacterId);

    if (result.success) {
      if (result.piecesClaimed > 0) {
        setClaimMessage(`Claimed ${result.piecesClaimed} pieces!`);
      } else {
        setClaimMessage('No rewards to claim');
      }
      loadReferralInfo();
    } else {
      setClaimMessage(result.error || 'Failed to claim rewards');
    }

    setIsClaiming(false);
  };

  const shareUrl = referralInfo?.referralCode 
    ? `${window.location.origin}?ref=${referralInfo.referralCode}`
    : '';

  const handleShare = (): void => {
    if (navigator.share && shareUrl) {
      navigator.share({
        title: 'Join Grand Online!',
        text: `Join me on Grand Online! Use my referral code ${referralInfo?.referralCode} to get 250,000 bonus pieces.`,
        url: shareUrl,
      }).catch(() => {
        handleCopyCode();
      });
    } else {
      handleCopyCode();
    }
  };

  if (!auth.user) {
    return (
      <div style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}>
        <div style={{
          background: '#0d1117',
          border: '2px solid #30363d',
          padding: '24px',
          maxWidth: '400px',
          width: '90%',
          fontFamily: 'var(--font-mono)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '16px', color: '#58a6ff', fontFamily: 'var(--font-mono)' }}>
              REFERRAL PROGRAM
            </h2>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: '1px solid #f85149',
                color: '#f85149',
                fontSize: '14px',
                cursor: 'pointer',
                padding: '4px 8px',
                fontFamily: 'var(--font-mono), monospace',
                fontWeight: 'bold',
              }}
            >
              x
            </button>
          </div>
          <p style={{ color: '#8b949e', fontSize: '12px' }}>
            Sign in to access the referral program and earn bonus pieces!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: '#0d1117',
        border: '2px solid #30363d',
        padding: '24px',
        maxWidth: '480px',
        width: '90%',
        fontFamily: 'var(--font-mono)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '16px', color: '#58a6ff', fontFamily: 'var(--font-mono)' }}>
            REFERRAL PROGRAM
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #f85149',
              color: '#f85149',
              fontSize: '14px',
              cursor: 'pointer',
              padding: '4px 8px',
              fontFamily: 'var(--font-mono), monospace',
              fontWeight: 'bold',
            }}
          >
            x
          </button>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px', color: '#8b949e' }}>
            Loading...
          </div>
        ) : (
          <>
            {/* Your Referral Code Section */}
            <div style={{
              background: '#161b22',
              border: '1px solid #21262d',
              padding: '16px',
              marginBottom: '16px',
            }}>
              <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase' }}>
                Your Referral Code
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div style={{
                  flex: 1,
                  background: '#0d1117',
                  border: '1px solid #30363d',
                  padding: '12px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#f0883e',
                  letterSpacing: '2px',
                  textAlign: 'center',
                  fontFamily: 'var(--font-mono)',
                }}>
                  {referralInfo?.referralCode || '--------'}
                </div>
                <button
                  onClick={handleCopyCode}
                  style={{
                    padding: '12px 16px',
                    background: copied ? '#238636' : '#21262d',
                    border: `1px solid ${copied ? '#2ea043' : '#30363d'}`,
                    color: '#fff',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 'bold',
                  }}
                >
                  {copied ? 'COPIED' : 'COPY'}
                </button>
              </div>
              <button
                onClick={handleShare}
                style={{
                  width: '100%',
                  marginTop: '12px',
                  padding: '10px',
                  background: '#238636',
                  border: '1px solid #2ea043',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                SHARE WITH FRIENDS
              </button>
              <div style={{ fontSize: '11px', color: '#8b949e', marginTop: '8px', textAlign: 'center' }}>
                Both you and your friend get 250,000 pieces!
              </div>
            </div>

            {/* Stats Section */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '12px',
              marginBottom: '16px',
            }}>
              <div style={{
                background: '#161b22',
                border: '1px solid #21262d',
                padding: '12px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#58a6ff' }}>
                  {referralInfo?.referralCount || 0}
                </div>
                <div style={{ fontSize: '10px', color: '#8b949e', textTransform: 'uppercase' }}>
                  Friends Referred
                </div>
              </div>
              <div style={{
                background: '#161b22',
                border: '1px solid #21262d',
                padding: '12px',
                textAlign: 'center',
              }}>
                <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f0883e' }}>
                  {referralInfo?.pendingRewards || 0}
                </div>
                <div style={{ fontSize: '10px', color: '#8b949e', textTransform: 'uppercase' }}>
                  Pending Rewards
                </div>
              </div>
            </div>

            {/* Claim Rewards Button */}
            {(referralInfo?.pendingRewards || 0) > 0 && (
              <button
                onClick={handleClaimRewards}
                disabled={isClaiming}
                style={{
                  width: '100%',
                  padding: '12px',
                  marginBottom: '16px',
                  background: '#f0883e',
                  border: '1px solid #d18616',
                  color: '#000',
                  cursor: isClaiming ? 'wait' : 'pointer',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  opacity: isClaiming ? 0.7 : 1,
                }}
              >
                {isClaiming ? 'CLAIMING...' : 'CLAIM REWARDS'}
              </button>
            )}

            {claimMessage && (
              <div style={{
                padding: '8px',
                marginBottom: '16px',
                background: claimMessage.includes('Claimed') ? '#064e3b' : '#1e3a5f',
                border: `1px solid ${claimMessage.includes('Claimed') ? '#10b981' : '#3b82f6'}`,
                color: claimMessage.includes('Claimed') ? '#34d399' : '#60a5fa',
                fontSize: '11px',
                textAlign: 'center',
              }}>
                {claimMessage}
              </div>
            )}

            {/* Enter Referral Code Section */}
            {!referralInfo?.referredBy && (
              <div style={{
                background: '#161b22',
                border: '1px solid #21262d',
                padding: '16px',
              }}>
                <div style={{ fontSize: '11px', color: '#8b949e', marginBottom: '8px', textTransform: 'uppercase' }}>
                  Have a Referral Code?
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={referralCodeInput}
                    onChange={(e) => setReferralCodeInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.stopPropagation()}
                    placeholder="Enter code"
                    maxLength={8}
                    style={{
                      flex: 1,
                      padding: '10px',
                      background: '#0d1117',
                      border: '1px solid #30363d',
                      color: '#e2e8f0',
                      fontSize: '14px',
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                    }}
                  />
                  <button
                    onClick={handleApplyCode}
                    disabled={isApplying || !referralCodeInput.trim()}
                    style={{
                      padding: '10px 16px',
                      background: '#238636',
                      border: '1px solid #2ea043',
                      color: '#fff',
                      cursor: isApplying || !referralCodeInput.trim() ? 'not-allowed' : 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      opacity: isApplying || !referralCodeInput.trim() ? 0.5 : 1,
                    }}
                  >
                    {isApplying ? '...' : 'APPLY'}
                  </button>
                </div>
                {applyError && (
                  <div style={{
                    marginTop: '8px',
                    padding: '6px',
                    background: '#3d1a1a',
                    border: '1px solid #f85149',
                    color: '#f85149',
                    fontSize: '11px',
                  }}>
                    {applyError}
                  </div>
                )}
                {applySuccess && (
                  <div style={{
                    marginTop: '8px',
                    padding: '6px',
                    background: '#064e3b',
                    border: '1px solid #10b981',
                    color: '#34d399',
                    fontSize: '11px',
                  }}>
                    {applySuccess}
                  </div>
                )}
              </div>
            )}

            {referralInfo?.referredBy && (
              <div style={{
                background: '#064e3b',
                border: '1px solid #10b981',
                padding: '12px',
                textAlign: 'center',
                color: '#34d399',
                fontSize: '11px',
              }}>
                You were referred by a friend - thanks for joining!
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
